import { message } from "utils/ciq-message";
import ErrorBoundary from "components/error-boundary";
import FeatureLinkingDialog from "components/feature-linking-dialog";
import ActivitiesTable from "components/feature-linking-dialog/activities-table";
import ActivityLinksDialogHeader from "components/feature-linking-dialog/activity-links-dialog-header";
import LinkedActivitiesList from "components/feature-linking-dialog/linked-activities-list";
import {
  ActivityItem,
  Schedule,
  useFeatureActivityLinking
} from "entity-app/hooks/feature-activity-linking";
import { useCallback, useEffect, useMemo, useState } from "react";

function buildActivityLinkChanges(
  selectedActivities: ActivityItem[],
  linkedActivities: ActivityItem[] | undefined
): {
  insertedLinks: ActivityItem[];
  updatedLinks: ActivityItem[];
  deletedLinks: ActivityItem[];
} {
  const insertedLinks: ActivityItem[] = [];
  const updatedLinks: ActivityItem[] = [];
  const deletedLinks: ActivityItem[] = [];

  const linkedActivitiesMap = new Map(
    linkedActivities?.map((activity) => [activity.id, activity])
  );

  selectedActivities.forEach((activity) => {
    const existingActivity = linkedActivitiesMap.get(activity.id);
    if (existingActivity) {
      if (existingActivity.link_type !== activity.link_type) {
        updatedLinks.push(activity);
      }
      linkedActivitiesMap.delete(activity.id);
    } else {
      insertedLinks.push(activity);
    }
  });

  linkedActivitiesMap.forEach((activity) => {
    deletedLinks.push(activity);
  });

  return { insertedLinks, updatedLinks, deletedLinks };
}

function generateActivityLinkPayload(
  featureId: string | undefined,
  changes: ReturnType<typeof buildActivityLinkChanges>
) {
  if (!featureId) {
    return null;
  }

  const { insertedLinks, updatedLinks, deletedLinks } = changes;
  const links = [...insertedLinks, ...updatedLinks];

  return {
    bid_package_ids: [featureId],
    ...(links.length && {
      link_activities: links.map((activity) => ({
        id: activity.id,
        link_type: activity.link_type
      }))
    }),
    ...(deletedLinks.length && {
      unlink_activity_ids: deletedLinks.map((activity) => activity.id)
    })
  };
}

function FeatureActivityLinking({
  dialogOpenState,
  featureId,
  linkedFeatureId,
  bidPackageName,
  onSaveSuccess
}: {
  dialogOpenState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  featureId: string | undefined;
  linkedFeatureId: string | undefined;
  bidPackageName?: string;
  onSaveSuccess: () => void;
}) {
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null
  );
  const [, setIsActivityLinkingDialogOpen] = dialogOpenState;

  const [inEditMode, setInEditMode] = useState(false);

  const [activitySelectionChanged, setActivitySelectionChanged] =
    useState(false);

  const [isModalOpen, setIsModalOpen] = useState(true);

  const {
    activeSchedule,
    scheduleList,
    activitiesTree,
    linkedActivities,
    isLoadingScheduleList,
    isLoadingLinkedActivities,
    resetStates,
    updateFeatureLinks,
    isUpdatingFeatureLinks
  } = useFeatureActivityLinking({
    featureId: linkedFeatureId,
    selectedSchedule,
    skip: !isModalOpen || inEditMode
  });

  const [selectedActivities, setSelectedActivities] = useState<ActivityItem[]>(
    []
  );

  useEffect(() => {
    if (linkedActivities) {
      setSelectedActivities(structuredClone(linkedActivities));
    }
  }, [linkedActivities]);

  useEffect(() => {
    if (activeSchedule) {
      setSelectedSchedule(activeSchedule);
    }
  }, [activeSchedule]);

  useEffect(() => {
    if (activeSchedule || selectedSchedule || isLoadingScheduleList) {
      return;
    }
    if (scheduleList?.length === 1) {
      setSelectedSchedule(scheduleList[0]);
    }
  }, [activeSchedule, isLoadingScheduleList, scheduleList, selectedSchedule]);

  const onCheckBoxAction = ({
    checked,
    activity
  }: {
    checked: boolean;
    activity: ActivityItem;
  }) => {
    setInEditMode(true);
    setActivitySelectionChanged(true);
    setSelectedActivities((prevActivities) => {
      if (checked) {
        const existingIndex = prevActivities.findIndex(
          (a) => a.id === activity.id
        );

        if (existingIndex !== -1) {
          // Update existing activity
          return prevActivities.map((a, index) =>
            index === existingIndex ? activity : a
          );
        }
        // Add new activity
        const updatedActivity = { ...activity, newlyLinked: true };
        return [updatedActivity, ...prevActivities];
      }
      // Remove activity
      return prevActivities.filter((a) => a.id !== activity.id);
    });
  };

  const onUnlink = useCallback((activity: ActivityItem) => {
    onCheckBoxAction({
      checked: false,
      activity
    });
  }, []);

  // useEffect(() => {
  // }, [activeSchedule, selectedActivities, selectedSchedule]);

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setIsActivityLinkingDialogOpen(false);
    }, 500);
  };

  const onSave = async () => {
    const { insertedLinks, updatedLinks, deletedLinks } =
      buildActivityLinkChanges(selectedActivities, linkedActivities);

    const linkingPayload = generateActivityLinkPayload(featureId, {
      insertedLinks,
      updatedLinks,
      deletedLinks
    });
    if (!linkingPayload) return;

    const response = await updateFeatureLinks(linkingPayload);

    if (response.success) {
      message.success("Updated successfully.");

      setInEditMode(false);
      closeModal();
      resetStates();
      onSaveSuccess();
    }
  };

  const linkedActivitiesDOM = useMemo(() => {
    if (isLoadingLinkedActivities) {
      return null;
    }
    if (selectedActivities && selectedActivities.length) {
      return (
        <ErrorBoundary>
          <LinkedActivitiesList
            linkedActivities={selectedActivities}
            onUnlink={onUnlink}
          />
        </ErrorBoundary>
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        <div className="text-ciq-gray-950 text-opacity-80 text-xs italic relative">
          Select an Activity to link.
        </div>
      </div>
    );
  }, [isLoadingLinkedActivities, onUnlink, selectedActivities]);

  return (
    <FeatureLinkingDialog
      open={isModalOpen}
      headerComponent={
        <ErrorBoundary>
          <ActivityLinksDialogHeader
            selectedSchedule={selectedSchedule}
            bidPackageName={bidPackageName}
            scheduleList={scheduleList}
            onScheduleChange={setSelectedSchedule}
            onCancel={() => {
              closeModal();
              setInEditMode(false);
              resetStates();
            }}
            onSave={() => {
              onSave();
            }}
            isLoadingScheduleList={isLoadingScheduleList}
            isSaving={isUpdatingFeatureLinks}
            saveButtonDisabled={!activitySelectionChanged}
          />
        </ErrorBoundary>
      }
      lhsComponent={
        <div className="h-full max-h-full overflow-hidden flex flex-col">
          <div className="my-5 uppercase text-xs px-4">
            Linked Activities{" "}
            {selectedActivities?.length
              ? `(${selectedActivities?.length})`
              : ""}
          </div>
          <div className="grow overflow-y-auto px-4">{linkedActivitiesDOM}</div>
        </div>
      }
      rhsComponent={
        <ErrorBoundary>
          <ActivitiesTable
            activitiesTree={activitiesTree}
            linkedActivities={selectedActivities}
            onCheckBoxAction={onCheckBoxAction}
            selectedSchedule={selectedSchedule}
            isLoadingScheduleList={isLoadingScheduleList}
            hasSchedules={Boolean(scheduleList?.length)}
          />
        </ErrorBoundary>
      }
    />
  );
}

export default FeatureActivityLinking;
